import { useState } from "react";
import { Tag } from "lucide-react";
import { Button } from "@cieda/djayma-ui";
import { toast } from "sonner";
import { AdminModal } from "@/components/admin/shared";
import {
  useCreatePromotion,
  useUpdatePromotion,
} from "@/hooks/admin/useMarketing";
import type {
  Promotion,
  PromoType,
  CreatePromotionInput,
} from "@/lib/admin-api/marketing";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PROMO_TYPE_LABELS: Record<PromoType, string> = {
  percentage: "Pourcentage",
  fixed_amount: "Montant fixe (FCFA)",
  free_shipping: "Livraison gratuite",
  buy_x_get_y: "Achetez X, obtenez Y",
};

const PROMO_TYPE_DESCRIPTIONS: Record<PromoType, string> = {
  percentage: "Remise en % sur le total",
  fixed_amount: "Remise d'un montant fixe en FCFA",
  free_shipping: "Livraison offerte sur la commande",
  buy_x_get_y: "Offre groupee : achetez X, recevez Y gratuit",
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface PromoFormModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly initial?: Partial<Promotion>;
}

export function PromoFormModal({ open, onClose, initial }: PromoFormModalProps) {
  const isEdit = initial?.id != null;
  const create = useCreatePromotion();
  const update = useUpdatePromotion();

  const [name, setName] = useState(initial?.name ?? "");
  const [type, setType] = useState<PromoType>(initial?.type ?? "percentage");
  const [discountValue, setDiscountValue] = useState(
    String(initial?.discount_value ?? ""),
  );
  const [minOrder, setMinOrder] = useState(
    String(initial?.min_order_fcfa ?? ""),
  );
  const [maxUses, setMaxUses] = useState(String(initial?.max_uses ?? ""));
  const [startsAt, setStartsAt] = useState(
    initial?.starts_at?.slice(0, 10) ?? "",
  );
  const [endsAt, setEndsAt] = useState(initial?.ends_at?.slice(0, 10) ?? "");
  const [productIds, setProductIds] = useState(
    initial?.applicable_products?.join(", ") ?? "",
  );
  const [categoryIds, setCategoryIds] = useState(
    initial?.applicable_categories?.join(", ") ?? "",
  );

  const isPending = create.isPending || update.isPending;

  const handleSubmit = () => {
    if (!name.trim()) {
      toast.error("Le nom est obligatoire.");
      return;
    }

    if (type !== "free_shipping") {
      const dv = parseFloat(discountValue);
      if (isNaN(dv) || dv < 0) {
        toast.error("Valeur de remise invalide.");
        return;
      }
      if (type === "percentage" && dv > 100) {
        toast.error("Le pourcentage ne peut pas depasser 100 %.");
        return;
      }
    }

    if (!startsAt) {
      toast.error("La date de début est obligatoire.");
      return;
    }
    if (endsAt && new Date(endsAt) < new Date(startsAt)) {
      toast.error("La date de fin doit être postérieure à la date de début.");
      return;
    }

    const parsedProducts = productIds
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    const parsedCategories = categoryIds
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    const input: CreatePromotionInput = {
      name,
      type,
      discount_value: type === "free_shipping" ? 0 : parseFloat(discountValue),
      min_order_fcfa: minOrder ? parseFloat(minOrder) : null,
      max_uses: maxUses ? parseInt(maxUses, 10) : null,
      starts_at: startsAt,
      ends_at: endsAt || null,
      applicable_products:
        parsedProducts.length > 0 ? parsedProducts : undefined,
      applicable_categories:
        parsedCategories.length > 0 ? parsedCategories : undefined,
    };

    if (isEdit && initial?.id) {
      update.mutate(
        { id: initial.id, input },
        {
          onSuccess: () => {
            toast.success("Promotion mise a jour.");
            onClose();
          },
          onError: () => toast.error("Erreur lors de la mise a jour."),
        },
      );
    } else {
      create.mutate(input, {
        onSuccess: () => {
          toast.success("Promotion creee.");
          onClose();
        },
        onError: () => toast.error("Erreur lors de la creation."),
      });
    }
  };

  return (
    <AdminModal
      open={open}
      onClose={onClose}
      title={isEdit ? "Modifier la promotion" : "Nouvelle promotion"}
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={isPending}>
            {isPending ? "En cours..." : isEdit ? "Mettre a jour" : "Creer"}
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        {/* Name */}
        <div className="space-y-1">
          <label className="text-sm font-medium text-stone-700">
            Nom <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ex : Promo Ramadan 25%"
            className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
          />
        </div>

        {/* Type selector as cards */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-stone-700">
            Type de promotion <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-2 gap-2">
            {(Object.keys(PROMO_TYPE_LABELS) as PromoType[]).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setType(t)}
                className={`rounded-lg border p-3 text-left transition-all ${
                  type === t
                    ? "border-amber-400 bg-amber-50 ring-1 ring-amber-300"
                    : "border-stone-200 bg-white hover:border-stone-300"
                }`}
              >
                <p className="text-sm font-medium text-stone-800">
                  {PROMO_TYPE_LABELS[t]}
                </p>
                <p className="text-xs text-stone-500 mt-0.5">
                  {PROMO_TYPE_DESCRIPTIONS[t]}
                </p>
              </button>
            ))}
          </div>
        </div>

        {/* Value + constraints */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {type !== "free_shipping" && (
            <div className="space-y-1">
              <label className="text-sm font-medium text-stone-700">
                Valeur remise <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={discountValue}
                  onChange={(e) => setDiscountValue(e.target.value)}
                  placeholder={
                    type === "percentage" ? "ex : 20" : "ex : 5000"
                  }
                  min={0}
                  max={type === "percentage" ? 100 : undefined}
                  className="w-full rounded-lg border border-stone-200 px-3 py-2 pr-16 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-stone-400">
                  {type === "percentage" ? "%" : "FCFA"}
                </span>
              </div>
            </div>
          )}
          <div className="space-y-1">
            <label className="text-sm font-medium text-stone-700">
              Commande min. (FCFA)
            </label>
            <input
              type="number"
              value={minOrder}
              onChange={(e) => setMinOrder(e.target.value)}
              placeholder="ex : 10 000"
              min={0}
              className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-stone-700">
              Max utilisations
            </label>
            <input
              type="number"
              value={maxUses}
              onChange={(e) => setMaxUses(e.target.value)}
              placeholder="Illimite si vide"
              min={1}
              className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
            />
          </div>
        </div>

        {/* Date range */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-1">
            <label className="text-sm font-medium text-stone-700">
              Debut <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={startsAt}
              onChange={(e) => setStartsAt(e.target.value)}
              className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-stone-700">
              Fin (optionnel)
            </label>
            <input
              type="date"
              value={endsAt}
              onChange={(e) => setEndsAt(e.target.value)}
              min={startsAt || undefined}
              className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
            />
          </div>
        </div>

        {/* Product / Category targeting */}
        <div className="space-y-4 rounded-lg border border-stone-200 bg-stone-50 p-4">
          <div className="flex items-center gap-2">
            <Tag className="h-4 w-4 text-stone-500" />
            <p className="text-sm font-medium text-stone-700">
              Ciblage (optionnel)
            </p>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-stone-600">
              IDs produits (separes par des virgules)
            </label>
            <textarea
              value={productIds}
              onChange={(e) => setProductIds(e.target.value)}
              rows={2}
              placeholder="Laisser vide pour appliquer a tous les produits"
              className="w-full resize-none rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-amber-300"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-stone-600">
              IDs categories (separes par des virgules)
            </label>
            <textarea
              value={categoryIds}
              onChange={(e) => setCategoryIds(e.target.value)}
              rows={2}
              placeholder="Laisser vide pour appliquer a toutes les categories"
              className="w-full resize-none rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-amber-300"
            />
          </div>
        </div>
      </div>
    </AdminModal>
  );
}
